import { useState, useEffect } from 'react';
import { ItemFilters, DisplayMode, ActiveFunctions } from '@/types/item';
import { useLocalStorageManager } from './useLocalStorageManager';
import { logger } from '@/lib/logger';

// Default values for filters
const DEFAULT_FILTERS: ItemFilters = {
  search: '',
  activeGates: {
    g1: true,
    g2: true,
    g3: true,
    g4: true,
    g5: true,
  },
  status: [],
  sortBy: 'custom',
  sortDirection: 'asc',
};

const DEFAULT_ACTIVE_FUNCTIONS: ActiveFunctions = {
  shop: false,
  specs: false,
  po: false,
  contract: false, // Default OFF per user requirement
};

// Validate and sanitize filters data
const validateFilters = (filters: any): ItemFilters => {
  if (!filters || typeof filters !== 'object') {
    return DEFAULT_FILTERS;
  }

  // Migrate old activeStatuses object format to new status array format
  let migratedStatus: string[] = [];
  if (filters.status && Array.isArray(filters.status)) {
    migratedStatus = filters.status;
  } else if (filters.activeStatuses && typeof filters.activeStatuses === 'object') {
    // Migration from old format
    migratedStatus = Object.entries(filters.activeStatuses)
      .filter(([_, active]) => active === true)
      .map(([status]) => status);
  }

  return {
    search: typeof filters.search === 'string' ? filters.search.substring(0, 1000) : '',
    categories: Array.isArray(filters.categories) ? filters.categories : undefined,
    category: typeof filters.category === 'string' ? filters.category : undefined,
    activeGates: DEFAULT_FILTERS.activeGates,
    status: migratedStatus,
    activeMilestoneTypes: Array.isArray(filters.activeMilestoneTypes) ? filters.activeMilestoneTypes : undefined,
    milestoneName: typeof filters.milestoneName === 'string' ? filters.milestoneName : undefined,
    sortBy: ['custom', 'start', 'end'].includes(filters.sortBy) ? filters.sortBy : 'custom',
    sortDirection: ['asc', 'desc'].includes(filters.sortDirection) ? filters.sortDirection : 'asc',
  };
};

const validateActiveFunctions = (activeFunctions: any): ActiveFunctions => {
  if (!activeFunctions || typeof activeFunctions !== 'object') {
    return DEFAULT_ACTIVE_FUNCTIONS;
  }

  return {
    shop: typeof activeFunctions.shop === 'boolean' ? activeFunctions.shop : true,
    specs: typeof activeFunctions.specs === 'boolean' ? activeFunctions.specs : true,
    po: typeof activeFunctions.po === 'boolean' ? activeFunctions.po : true,
    contract: typeof activeFunctions.contract === 'boolean' ? activeFunctions.contract : false,
  };
};

const validateDisplayMode = (displayMode: any): DisplayMode => {
  return displayMode === 'absolute' || displayMode === 'normalized' ? displayMode : 'normalized';
};

export interface ItemsFiltersState {
  filters: ItemFilters;
  activeFunctions: ActiveFunctions;
  displayMode: DisplayMode;
  updateFilters: (newFilters: ItemFilters) => void;
  updateActiveFunctions: (newActiveFunctions: ActiveFunctions) => void;
  updateDisplayMode: (newDisplayMode: DisplayMode) => void;
  clearFilters: () => void;
  getActiveFiltersCount: () => number;
  isDefaultState: boolean;
}

export const useItemsFiltersState = (): ItemsFiltersState => {
  const { getStandardValue, setStandardValue } = useLocalStorageManager();
  
  // Initialize state from localStorage with validation
  const [filters, setFilters] = useState<ItemFilters>(() => {
    const saved = getStandardValue('items-filters', DEFAULT_FILTERS, 'USER');
    const validated = validateFilters(saved);
    logger.debug('[ItemsFiltersState] Initialized filters:', { saved, validated });
    return validated;
  });

  const [activeFunctions, setActiveFunctions] = useState<ActiveFunctions>(DEFAULT_ACTIVE_FUNCTIONS);

  const [displayMode, setDisplayMode] = useState<DisplayMode>(() => {
    const saved = getStandardValue('items-displayMode', 'normalized' as DisplayMode, 'USER');
    const validated = validateDisplayMode(saved);
    logger.debug('[ItemsFiltersState] Initialized displayMode:', { saved, validated });
    return validated;
  });

  // Persist filters to localStorage with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setStandardValue('items-filters', filters, 'USER');
      logger.debug('[ItemsFiltersState] Persisted filters to localStorage');
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters, setStandardValue]);


  // Persist displayMode to localStorage
  useEffect(() => {
    setStandardValue('items-displayMode', displayMode, 'USER');
    logger.debug('[ItemsFiltersState] Persisted displayMode to localStorage');
  }, [displayMode, setStandardValue]);

  const updateFilters = (newFilters: ItemFilters) => {
    const validated = validateFilters(newFilters);
    setFilters(validated);
    logger.debug('[ItemsFiltersState] Updated filters:', validated);
  };

  const updateActiveFunctions = (newActiveFunctions: ActiveFunctions) => {
    const validated = validateActiveFunctions(newActiveFunctions);
    setActiveFunctions(validated);
    logger.debug('[ItemsFiltersState] Updated activeFunctions:', validated);
  };

  const updateDisplayMode = (newDisplayMode: DisplayMode) => {
    const validated = validateDisplayMode(newDisplayMode);
    setDisplayMode(validated);
    logger.debug('[ItemsFiltersState] Updated displayMode:', validated);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setActiveFunctions(DEFAULT_ACTIVE_FUNCTIONS);
    setDisplayMode('normalized');
    logger.debug('[ItemsFiltersState] Cleared all filters');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.category) count++;
    if (filters.categories !== undefined) count++;
    
    // Check if any gates are disabled
    const allGatesActive = Object.values(filters.activeGates).every(Boolean);
    if (!allGatesActive) count++;
    
    // Check if any functions are disabled
    const allFunctionsActive = Object.values(activeFunctions).every(Boolean);
    if (!allFunctionsActive) count++;
    
    // Check if any statuses are selected
    if (filters.status && filters.status.length > 0) count++;
    
    return count;
  };

  const isDefaultState = () => {
    return (
      filters.search === DEFAULT_FILTERS.search &&
      filters.category === undefined &&
      filters.categories === undefined &&
      JSON.stringify(filters.activeGates) === JSON.stringify(DEFAULT_FILTERS.activeGates) &&
      (!filters.status || filters.status.length === 0) &&
      JSON.stringify(activeFunctions) === JSON.stringify(DEFAULT_ACTIVE_FUNCTIONS) &&
      displayMode === 'normalized'
    );
  };

  return {
    filters,
    activeFunctions,
    displayMode,
    updateFilters,
    updateActiveFunctions,
    updateDisplayMode,
    clearFilters,
    getActiveFiltersCount,
    isDefaultState: isDefaultState(),
  };
};